import type { VoiceContextV1 } from '../api/qwen-realtime';
import type { Message } from '../types/domain';
import imageNameLookup from '../data/image-name-lookup.json';

interface VoiceRouteState {
  topicHint?: {
    argomentoId: string;
    argomentoName?: string;
  };
}

const MAX_VISIBLE_IMAGES = 3;
const MAX_CONTEXT_JSON_CHARS = 4096;
const IMAGE_MARKER_RE = /\[\[image:(sign|question):([^\]]+)\]\]/gi;
const ANY_IMAGE_MARKER_RE = /\[\[image:[^\]]+\]\]/gi;

interface LookupEntry { imageId: string; italianName: string }
const LOOKUP = imageNameLookup as Record<string, LookupEntry>;

const VOICE_SIGN_ALIASES: Record<string, string> = {
  'passaggio a livello con barriere': 'passaggio a livello con barriere o semibarriere',
};

function normalizeSignName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveVoiceSignName(name: string): string {
  const alias = VOICE_SIGN_ALIASES[normalizeSignName(name)];
  if (!alias) return name;
  return LOOKUP[alias]?.italianName ?? name;
}

function capped(value: string, max: number): string {
  return value.trim().slice(0, max);
}

function stripImageMarkers(value: string): string {
  return value
    .replace(ANY_IMAGE_MARKER_RE, '')
    .replace(/\s+([:;,.!?])/g, '$1')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function appendUniqueImage(
  images: NonNullable<VoiceContextV1['currentVisibleImages']>,
  image: NonNullable<VoiceContextV1['currentVisibleImages']>[number],
): void {
  if (images.length >= MAX_VISIBLE_IMAGES) return;
  if (images.some((candidate) => candidate.kind === image.kind && candidate.id === image.id)) return;
  images.push(image);
}

function latestQuestionMessage(messages: Message[]): Extract<Message, { kind: 'question' }> | null {
  const message = latestProfMessage(messages);
  if (!message || message.authorKind !== 'prof') return null;
  if (message.kind === 'question' && !message.answered) return message;
  return null;
}

function latestProfTextQuestion(messages: Message[]): string | null {
  const message = latestProfMessage(messages);
  if (!message || message.authorKind !== 'prof' || !('text' in message)) return null;
  const text = stripImageMarkers(message.text);
  if (!text || text.length > 800 || !/\?\s*$/.test(text)) return null;
  const normalized = text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (/\b(?:tutto chiaro|va bene cosi)\?\s*$/.test(normalized)) return null;
  if (
    /\bvero\s+o\s+falso\b/i.test(normalized) ||
    /\b(rispondi|dimmi|prova|cosa fai|che cosa fai)\b/i.test(normalized) ||
    /\b(qual e|quale|secondo te|sai dirmi|differenza|cosa succede|cosa cambia)\b/i.test(normalized) ||
    /\bresta\b.*\bo\b.*\bscende\b/i.test(normalized)
  ) {
    return text;
  }
  return null;
}

function latestProfMessage(messages: Message[]): Message | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.authorKind === 'prof') return message;
  }
  return null;
}

function latestImagesOnlyProfIndex(messages: Message[]): number | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.authorKind !== 'prof') continue;
    if (message.kind !== 'text' || !('text' in message)) return null;
    if (!/\[\[image:(?:sign|question):[^\]]+\]\]/i.test(message.text)) return null;
    const stripped = stripImageMarkers(message.text);
    return stripped.length <= 12 ? i : null;
  }
  return null;
}

function latestUnresolvedStudentIntentFromImagesOnlyTurn(messages: Message[]): string | null {
  const profIndex = latestImagesOnlyProfIndex(messages);
  if (profIndex === null) return null;
  for (let i = profIndex - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.authorKind === 'prof') return null;
    if (message.authorKind === 'you' && message.kind === 'text' && 'text' in message) {
      const text = stripImageMarkers(message.text);
      return text && text.length <= 500 ? text : null;
    }
  }
  return null;
}

function collectVisibleImages(messages: Message[]): NonNullable<VoiceContextV1['currentVisibleImages']> {
  const images: NonNullable<VoiceContextV1['currentVisibleImages']> = [];
  const message = latestProfMessage(messages);
  if (!message) return images;

  if (message.kind === 'question' && message.payload.imageRef) {
    appendUniqueImage(images, {
      kind: 'question',
      id: capped(message.payload.imageRef, 120),
      alt: 'Segnale stradale',
    });
    return images;
  }

  if (!('text' in message)) return images;
  for (const match of message.text.matchAll(IMAGE_MARKER_RE)) {
    const kind = match[1] === 'question' ? 'question' : 'sign';
    const rawId = capped(match[2] ?? '', 120);
    if (!rawId) continue;
    const id = kind === 'sign' ? capped(resolveVoiceSignName(rawId), 120) : rawId;
    appendUniqueImage(images, { kind, id, alt: capped(id, 200) });
    if (images.length >= MAX_VISIBLE_IMAGES) break;
  }
  return images;
}

export function buildVoiceContextFromProfState({
  messages,
  pathname,
  routeState,
}: {
  messages: Message[];
  pathname: string;
  routeState: VoiceRouteState | null;
}): VoiceContextV1 {
  const topicHint = routeState?.topicHint;
  const context: VoiceContextV1 = {
    version: 1,
    locale: 'it-IT',
    surface: 'prof',
    route: pathname.startsWith('/') ? pathname : '/prof',
    ...(topicHint?.argomentoName ? {
      topic: {
        id: topicHint.argomentoId,
        label: capped(topicHint.argomentoName, 120),
        source: 'route',
      },
    } : {}),
  };

  const question = latestQuestionMessage(messages);
  if (question) {
    const questionText = capped(stripImageMarkers(question.payload.text), 800);
    context.visiblePrompt = {
      kind: 'question',
      text: questionText || 'Domanda visibile nel Prof.',
      choices: ['Vero', 'Falso'],
      ...(question.payload.imageRef ? {
        imageId: capped(question.payload.imageRef, 120),
        imageAlt: 'Segnale stradale',
      } : {}),
    };
  } else {
    const promptText = latestProfTextQuestion(messages);
    if (promptText) {
      context.visiblePrompt = {
        kind: 'question',
        text: capped(promptText, 800),
        ...(/\bvero\s+o\s+falso\b/i.test(promptText) ? { choices: ['Vero', 'Falso'] } : {}),
      };
    } else {
      const unresolvedStudentIntent = latestUnresolvedStudentIntentFromImagesOnlyTurn(messages);
      if (unresolvedStudentIntent) {
        context.visiblePrompt = {
          kind: 'card',
          text: capped(`Domanda dello studente rimasta senza risposta: ${unresolvedStudentIntent}`, 800),
        };
      }
    }
  }

  const images = collectVisibleImages(messages);
  if (images.length > 0) context.currentVisibleImages = images;

  if (JSON.stringify(context).length <= MAX_CONTEXT_JSON_CHARS) return context;
  const compact = { ...context };
  delete compact.currentVisibleImages;
  if (JSON.stringify(compact).length <= MAX_CONTEXT_JSON_CHARS) return compact;
  delete compact.visiblePrompt;
  return compact;
}
